import type { MasterFlightRecord, PaginatedResponse, FlightQueryParams } from '../../../utils/types';
import { transformToApiFormat } from '../../../utils/types';
import { baseApi } from './baseApi';
import { customToast } from '../../../utils/toast';

// Ensures loading toast is visible long enough to be perceived by users
const MIN_LOADING_DELAY = 800;

async function withMinimumDelay<T>(promise: Promise<T>): Promise<T> {
  const start = Date.now();
  const result = await promise;
  const elapsed = Date.now() - start;
  if (elapsed < MIN_LOADING_DELAY) {
    await new Promise(resolve => setTimeout(resolve, MIN_LOADING_DELAY - elapsed));
  }
  return result;
}

/**
 * Normalizes station field casing from backend (handles both camelCase and PascalCase).
 * Backend may return either format depending on the data source (SSIM file vs manual entry).
 */
const normalizeStation = (station: any) => ({
  ...station,
  departureStation: station.departureStation ?? station.DepartureStation ?? '',
  arrivalStation: station.arrivalStation ?? station.ArrivalStation ?? '',
  passengerTerminalDep: station.passengerTerminalDep ?? station.PassengerTerminalDep ?? '',
  passengerTerminalArr: station.passengerTerminalArr ?? station.PassengerTerminalArr ?? '',
  std: station.std ?? station.Std ?? '',
  sta: station.sta ?? station.Sta ?? '',
  utcLocalTimeVariationDep: station.utcLocalTimeVariationDep ?? station.UtcLocalTimeVariationDep ?? '',
  utcLocalTimeVariationArr: station.utcLocalTimeVariationArr ?? station.UtcLocalTimeVariationArr ?? '',
  cd: station.cd ?? station.Cd ?? 0,
  ca: station.ca ?? station.Ca ?? 0,
  iataSubTypeCode: station.iataSubTypeCode ?? station.IataSubTypeCode ?? '',
  aircraftOwner: station.aircraftOwner ?? station.AircraftOwner ?? '',
  tailNo: station.tailNo ?? station.TailNo ?? '',
  aircraftConfiguration: station.aircraftConfiguration ?? station.AircraftConfiguration ?? '',
  onwardFlight: station.onwardFlight ?? station.OnwardFlight ?? '',
});

/**
 * Finds the first gap in variant IDs (e.g., [1,2,4] → 3) to reuse deleted slots.
 */
function findNextAvailableVariant(existingVariants: number[]): number {
  if (existingVariants.length === 0) return 1;
  const sorted = [...existingVariants].sort((a, b) => a - b);
  for (let i = 1; i <= sorted[sorted.length - 1]; i++) {
    if (!sorted.includes(i)) return i;
  }
  return sorted[sorted.length - 1] + 1;
}

export const masterFlightApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({

    getMasterFlights: builder.query<PaginatedResponse<MasterFlightRecord>, FlightQueryParams | void>({
      query: (params) => {
        const queryParams = params || {};
        const { page = 1, limit = 50, ...filters } = queryParams;
        const urlParams = new URLSearchParams({
          page: page.toString(),
          limit: limit.toString(),
          ...Object.entries(filters).reduce((acc, [key, value]) => {
            if (value !== undefined && value !== null) acc[key] = value.toString();
            return acc;
          }, {} as Record<string, string>)
        });
        return `/master-flight/with-codeshares?${urlParams}`;
      },

      transformResponse: (response: any): PaginatedResponse<MasterFlightRecord> => {
        const data = response.data.map((flight: any) => ({
          ...flight,
          stations: (flight.stations || []).map((station: any) => {
            const normalized = normalizeStation(station);
            return {
              ...normalized,
              // Backend sends `codeshares`, frontend uses `codeshareFlightNo`
              codeshareFlightNo: normalized.codeshareFlightNo || normalized.codeshares || [],
            };
          })
        }));
        return { data, meta: response.meta };
      },

      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ _id }) => ({ type: 'MasterFlight' as const, id: _id })),
              { type: 'MasterFlight', id: 'LIST' },
            ]
          : [{ type: 'MasterFlight', id: 'LIST' }],
    }),

    // Codeshare sync is handled separately in masterSchedule.tsx via useCodeshareSyncManager
    createMasterFlight: builder.mutation<MasterFlightRecord, MasterFlightRecord>({
      async queryFn(record, _queryApi, _extraOptions, fetchWithBQ) {
        try {
          const payload = transformToApiFormat(record);
          const flightResult = await fetchWithBQ({
            url: '/master-flight',
            method: 'POST',
            body: payload,
          });
          if (flightResult.error) return { error: flightResult.error };

          const raw = flightResult.data as any;
          return {
            data: {
              ...raw,
              stations: (raw.stations || []).map((s: any) => normalizeStation(s)),
            }
          };
        } catch (error: any) {
          return { error: { status: 'FETCH_ERROR', error: error.message } };
        }
      },

      async onQueryStarted(record, { queryFulfilled }) {
        const toastId = customToast.loading('Creating flight record...');
        try {
          await withMinimumDelay(queryFulfilled);
          customToast.success(`Flight ${record.flightNo} created successfully`, { id: toastId });
        } catch (error: any) {
          const errorMsg = error?.error?.data?.message || error?.error?.error || 'Unknown error';
          customToast.error(`Failed to create flight: ${errorMsg}`, { id: toastId });
        }
      },
      invalidatesTags: [{ type: 'MasterFlight', id: 'LIST' }],
    }),

    updateMasterFlight: builder.mutation<
      MasterFlightRecord,
      { id: string; record: MasterFlightRecord }
    >({
      async queryFn({ id, record }, _queryApi, _extraOptions, fetchWithBQ) {
        try {
          const payload = transformToApiFormat(record);
          const flightResult = await fetchWithBQ({
            url: `/master-flight/${id}`,
            method: 'PATCH',
            body: payload,
          });
          if (flightResult.error) return { error: flightResult.error };

          const raw = flightResult.data as any;
          return {
            data: {
              ...raw,
              stations: (raw.stations || []).map((s: any) => normalizeStation(s)),
            }
          };
        } catch (error: any) {
          return { error: { status: 'FETCH_ERROR', error: error.message } };
        }
      },

      async onQueryStarted({ id, record }, { queryFulfilled }) {
        const toastId = customToast.loading('Updating flight record...');
        try {
          await withMinimumDelay(queryFulfilled);
          customToast.success(`Flight ${record.flightNo} updated successfully`, { id: toastId });
        } catch (error: any) {
          const errorMsg = error?.error?.data?.message || error?.error?.error || 'Unknown error';
          customToast.error(`Failed to update flight: ${errorMsg}`, { id: toastId });
        }
      },
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'MasterFlight', id },
        { type: 'MasterFlight', id: 'LIST' }
      ],
    }),

    // Variant ID assignment: per-season airlines (MH, AA) reset each season;
    // global airlines (OD) continue across all seasons.
    checkDuplicateFlight: builder.query<
      { isDuplicate: boolean; existingVariants: number[]; maxVariant: number; nextAvailableVariant: number },
      { flightOwner: string; flightNo: string; seasonId: string; excludeId?: string }
    >({
      query: ({ flightOwner, flightNo, seasonId, excludeId }) => {
        const params = new URLSearchParams({
          flightOwner,
          flightNo: flightNo.replace(/\s/g, ''),
          seasonId,
        });
        if (excludeId) params.append('excludeId', excludeId);
        return `/master-flight/check-duplicate?${params}`;
      },
      transformResponse: (response: any) => {
        const variants = response.variants || [];
        return {
          isDuplicate: variants.length > 0,
          existingVariants: variants,
          maxVariant: variants.length > 0 ? Math.max(...variants) : 0,
          nextAvailableVariant: findNextAvailableVariant(variants),
        };
      },
    }),

    findByFlight: builder.query<MasterFlightRecord[], { flightNo: string; flightOwner: string; seasonId: string }>({
      query: ({ flightNo, flightOwner, seasonId }) => {
        const params = new URLSearchParams({ flightNo, flightOwner, seasonId });
        return `/master-flight/by-flight?${params}`;
      },
      transformResponse: (response: any): MasterFlightRecord[] => {
        const data = Array.isArray(response) ? response : response.data || [];
        return data.map((flight: any) => ({
          ...flight,
          stations: (flight.stations || []).map((s: any) => normalizeStation(s)),
        }));
      },
    }),

    deleteMasterFlight: builder.mutation<void, { id: string; flightNo: string }>({
      query: ({ id }) => ({
        url: `/master-flight/${id}`,
        method: 'DELETE',
      }),

      async onQueryStarted({ id, flightNo }, { queryFulfilled }) {
        const toastId = customToast.loading(`Deleting flight ${flightNo}...`);
        try {
          await withMinimumDelay(queryFulfilled);
          customToast.success(`Flight ${flightNo} deleted successfully`, { id: toastId });
        } catch (error: any) {
          const errorMsg = error?.error?.data?.message || error?.error?.error || 'Unknown error';
          customToast.error(`Failed to delete flight: ${errorMsg}`, { id: toastId });
        }
      },
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'MasterFlight', id },
        { type: 'MasterFlight', id: 'LIST' },
        { type: 'Codeshare', id: `MASTER_${id}` },
        'Codeshare',
      ],
    }),
  }),
});

export const {
  useGetMasterFlightsQuery,
  useLazyGetMasterFlightsQuery,
  useCreateMasterFlightMutation,
  useUpdateMasterFlightMutation,
  useDeleteMasterFlightMutation,
  useLazyCheckDuplicateFlightQuery,
  useLazyFindByFlightQuery,
} = masterFlightApi;
